import {
  Cog8ToothIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/20/solid'
import { useWallet } from '@solana/wallet-adapter-react'
import ConnectedMenu from '../wallet/ConnectedMenu'
import useOnlineStatus from 'hooks/useOnlineStatus'
import SettingsModal from '../modals/SettingsModal'
import Link from 'next/link'
import React, { useCallback, useState, PropsWithChildren } from 'react'
import { IconButton } from '../shared/Button'
import HeroSearch from '../search/HeroSearch'
import { LogoMark } from '../Logo'
import { useRouter } from 'next/router'
import store from 'stores/store'
import MobileNavButton from '../mobile/MobileNavButton'
import ConnectButton from '../shared/ConnectButton'
import GlowBadge from '@components/shared/GlowBadge'
import SeasonEndedBadge from '@components/shared/SeasonEndedBadge'
import { useTranslation } from 'next-i18next'
import LanguageSwitch from './LanguageSwitch'
import { clsx } from 'clsx'

const set = store.getState().set

const TopBar = () => {
  const { asPath } = useRouter()
  const { connected, publicKey, wallet } = useWallet()
  const [isSettingsModalShown, setIsSettingsModalShown] = useState(false)
  // const [isReferrerModalShown, setIsReferrerModalShown] = useState(false)
  const isOnline = useOnlineStatus()
  const { t } = useTranslation('topbar')

  const handleTokenStateReset = useCallback(() => {
    set((state) => {
      state.trade.limitForm.inputAmount = ''
      state.trade.limitForm.outputAmount = ''
      state.trade.limitForm.price = ''
    })
  }, [])

  const showSettingsModal = useCallback(() => setIsSettingsModalShown(true), [])
  const hideSettingsModal = useCallback(
    () => setIsSettingsModalShown(false),
    [],
  )

  // const showReferrerModal = useCallback(() => setIsReferrerModalShown(true), [])
  // const hideReferrerModal = useCallback(
  //   () => setIsReferrerModalShown(false),
  //   [],
  // )

  return (
    <>
      {!isOnline ? (
        <div className="fixed left-1/2 top-3 z-10 flex h-10 w-max -translate-x-1/2 items-center rounded-full bg-th-down px-4 py-2 md:top-8">
          <ExclamationTriangleIcon className="size-5 shrink-0 text-white" />
          <p className="ml-2 text-white">{t('offlineMessage')}</p>
        </div>
      ) : null}
      <div className="flex h-20 items-center border-b border-th-bkg-3 bg-th-bkg-1 px-4 md:px-6 lg:grid lg:grid-cols-[2fr_1fr_2fr]">
        <div className="flex flex-1 items-center justify-start gap-2 lg:gap-0">
          <div className="lg:hidden">
            <MobileNavButton />
          </div>
          <Link
            className="block h-6 w-max lg:h-7"
            href="/"
            onClick={handleTokenStateReset}
          >
            <LogoMark className="h-6 w-auto lg:h-7" />
          </Link>
          <div className="hidden lg:flex lg:items-center lg:space-x-5 lg:pl-5 xl:space-x-6 xl:pl-8">
            <TopBarLink
              href="/"
              isSelected={() => asPath.includes('/swap/') || asPath === '/'}
            >
              {t('swap')}
            </TopBarLink>
            <TopBarLink href="/trending" currentPath={asPath}>
              {t('trending')}
            </TopBarLink>
            <TopBarLink
              href="/leaderboard"
              currentPath={asPath}
              className="relative"
            >
              {t('leaderboard')}
              <SeasonEndedBadge
                text={t('badges.season-1-ended')}
                className="pointer-events-none absolute -right-7 -top-3 rotate-6 shadow-sm"
              />
            </TopBarLink>
            {/* HIDDEN_PROFILES_REFERRALS: revert via PROFILES_AND_REFERRALS_HIDDEN.md
            <TopBarLink href="/referrals" currentPath={asPath}>
              {t('referrals')}
            </TopBarLink>
            */}
            <TopBarLink href="/vaults" currentPath={asPath}>
              {t('vaults')}
            </TopBarLink>
            <TopBarLink href="/bluechip" currentPath={asPath}>
              {t('revenue')}
            </TopBarLink>
            {/* <TopBarLinkWithBadge
              href="/swap-contest"
              badgeText={t('badges.live')}
              currentPath={asPath}
            >
              {t('contest')}
            </TopBarLinkWithBadge> */}
          </div>
        </div>
        <div className="mr-2 flex items-center justify-end space-x-1 lg:mr-0 lg:justify-center lg:space-x-0">
          <HeroSearch />
        </div>
        <div className="flex items-center justify-end space-x-2">
          {/* {connected ? (
            <ReferrerDaoButton
              className="mr-2 hidden w-32 text-right text-xs font-medium hover:opacity-80 sm:flex sm:flex-col sm:items-end md:mr-0"
              onClick={showReferrerModal}
            />
          ) : null} */}
          <LanguageSwitch />
          <IconButton
            className="bg-th-bkg-2 md:hover:bg-th-bkg-3"
            onClick={showSettingsModal}
            size="small"
          >
            <Cog8ToothIcon className="size-5 text-th-fgd-1" />
          </IconButton>
          <div className="lg:pl-2">
            {connected && publicKey && wallet ? (
              <ConnectedMenu publicKey={publicKey} wallet={wallet} />
            ) : (
              <ConnectButton />
            )}
          </div>
        </div>
      </div>
      {isSettingsModalShown ? (
        <SettingsModal
          isOpen={isSettingsModalShown}
          onClose={hideSettingsModal}
        />
      ) : null}
      {/* {isReferrerModalShown ? (
        <ReferrerDaoModal
          isOpen={isReferrerModalShown}
          onClose={hideReferrerModal}
        />
      ) : null} */}
    </>
  )
}

export default TopBar

type TopBarLinkProps = PropsWithChildren<
  {
    href: string
    onClick?: () => void
    className?: string
    disabled?: boolean
  } & ({ currentPath: string } | { isSelected: () => boolean })
>

const TopBarLink: React.FC<TopBarLinkProps> = (props) => {
  const { href, onClick, children, className, disabled } = props
  return (
    <Link
      href={disabled ? '/' : href}
      onClick={disabled ? (e) => e.preventDefault() : onClick}
      className={clsx(
        className,
        'text-xs font-medium xl:text-sm',
        ('isSelected' in props && props.isSelected()) ||
          ('currentPath' in props && props.currentPath === href)
          ? 'text-th-fgd-1'
          : 'text-th-fgd-2',
        disabled && 'cursor-not-allowed',
      )}
    >
      {children}
    </Link>
  )
}

const TopBarLinkWithBadge: React.FC<
  TopBarLinkProps & { badgeText: string }
> = ({ badgeText, className, children, ...otherProps }) => (
  <TopBarLink
    {...otherProps}
    className={clsx(className, 'flex items-center gap-2')}
  >
    <div>{children}</div>
    <GlowBadge text={badgeText} />
  </TopBarLink>
)
